/**
 * Human-readable names, and the two formatters the screens share.
 *
 * Kept in one file so the list and the detail page cannot drift into calling
 * the same stage two different things.
 */

// Relative, not '@fitout/shared': npm workspace links are NTFS junctions and a
// junction cannot target a network path, so the workspace stubs are empty.
// See docs/ENVIRONMENT.md. esbuild bundles the TypeScript source directly.
import {
  ROLES,
  ROLE_LABELS,
  canHoldFinanceView,
  defaultFinanceView,
  type Role,
} from '../../../packages/shared/src/index.js';

import type { ProjectType } from './api.js';

export { ROLES, ROLE_LABELS, canHoldFinanceView, defaultFinanceView };
export type { Role };

/** A role's label, tolerating a key the shared package does not know. */
export function roleLabel(key: string): string {
  return ROLE_LABELS[key as Role] ?? key;
}

export const TYPE_LABELS: Readonly<Record<ProjectType, string>> = {
  office: 'Office',
  retail: 'Retail',
  f_and_b: 'F&B',
  residential: 'Residential',
  hospitality: 'Hospitality',
  other: 'Other',
};

/**
 * A project type's label, tolerating a key this bundle does not know — the
 * same idiom as `roleLabel`. Server data arrives as `string`, and an older
 * cached bundle meeting a newer type must show the raw key, not crash.
 */
export function typeLabel(key: string): string {
  return TYPE_LABELS[key as ProjectType] ?? key;
}

/** The eight lifecycle stages, short enough to fit the strip on a phone. */
export const STAGE_LABELS: Readonly<Record<string, string>> = {
  award: 'Award',
  design: 'Design',
  approvals: 'Approvals',
  procurement: 'Procurement',
  site_execution: 'Site',
  qa_qc: 'QA/QC',
  snagging: 'Snagging',
  handover: 'Handover',
};

/**
 * Stage status: a symbol AND a word, never a colour on its own.
 *
 * NFR AC7 (10-nfr-targets.md §4): status must survive being printed in black
 * and white, or read by someone who cannot distinguish red from green.
 *
 * `at_risk` READS AS "NEEDS ATTENTION", AND THE ENUM KEEPS ITS NAME.
 *
 * The Overview stacks the Health panel and the Lifecycle strip, and both list
 * "Approvals" — Health calling it Critical and Lifecycle calling it At risk.
 * Two states for one name on one screen, which a client reads as the app
 * contradicting itself rather than as the two different things they are: a
 * Pulse SIGNAL's severity, and where the STAGE has got to.
 *
 * "At risk" was doing five jobs — signals, project health, material risk,
 * milestones and stages. Only two of them ever share a screen, so the stage is
 * the one that gives the words up. The column, the enum, the state machines and
 * the API all still say `at_risk`; only the word a person reads has changed.
 */
export const STAGE_STATUS: Readonly<Record<string, { symbol: string; label: string }>> = {
  complete: { symbol: '✓', label: 'Complete' },
  active: { symbol: '●', label: 'In progress' },
  at_risk: { symbol: '▲', label: 'Needs attention' },
  blocked: { symbol: '■', label: 'Blocked' },
  not_started: { symbol: '○', label: 'Not started' },
};

export const PROJECT_STATUS_LABELS: Readonly<Record<string, string>> = {
  active: 'Active',
  on_hold: 'On hold',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * `2026-09-01` → `1 Sep 2026`.
 *
 * Split, not `new Date(...)`: a date-only string is parsed as UTC midnight, so
 * in any timezone behind UTC the browser would show the day before. A start
 * date that moves depending on where you are standing is not acceptable.
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return iso;
  const [, year, month, day] = match;
  return `${Number(day)} ${MONTHS[Number(month) - 1]} ${year}`;
}

/**
 * How long a snag or an NCR has been open, for a cell that is already labelled.
 *
 * `0` is a real and common answer — most records are looked at on the day they
 * are raised — and "0 days" is a clumsy way to give it. Nothing has been open
 * for no days; it was raised today.
 */
export function openFor(days: number): string {
  return days <= 0 ? 'today' : `${days} ${days === 1 ? 'day' : 'days'}`;
}

/** The same, where it has to stand as a phrase: "raised today", "12 days open". */
export function openForPhrase(days: number): string {
  return days <= 0 ? 'raised today' : `${openFor(days)} open`;
}

/** How long ago something happened: "today", "1 day ago", "12 days ago". */
export function daysAgo(days: number): string {
  return days <= 0 ? 'today' : `${openFor(days)} ago`;
}

/**
 * A stored money string as a FORM FIELD should show it: `315000.0000` → `315000.00`.
 *
 * Not `formatMoney`, which groups with commas for reading — this is for an
 * input somebody is about to edit, and grouping would have to be stripped back
 * out on the way in. What it removes is the four-decimal STORAGE precision from
 * a box a person types into: the payment field offered "315000.0000", which is
 * how the column holds it and not how anybody writes it.
 *
 * IT NEVER DROPS MONEY. Trailing zeros go; real precision beyond two places is
 * left exactly as it was, because silently truncating 100.1250 to 100.12 in a
 * field the person is about to submit would lose half a fils on the way back.
 *
 * String surgery and never `Number()`, for the reason `formatMoney` gives.
 */
export function moneyForInput(value: string | null | undefined): string {
  if (!value) return '';
  const [whole = '0', fraction = ''] = value.split('.');
  const meaningful = fraction.replace(/0+$/, '');
  if (meaningful.length > 2) return value;
  return `${whole}.${(meaningful + '00').slice(0, 2)}`;
}

/**
 * `1250000.0000` → `1,250,000.00`.
 *
 * Grouped by string surgery, never by `Number(...)`: money is a decimal string
 * from the database column to the screen, and a float in the middle of that
 * journey is how 1,250,000.00 quietly becomes 1,249,999.99 (07 §DI2).
 */
export function formatMoney(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const [whole = '0', fraction = ''] = value.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${grouped}.${(fraction + '00').slice(0, 2)}`;
}
